// src/pages/Delivery.jsx

import React, { useState, useEffect, useCallback } from 'react';
import PrivateRoute from "../routes/PrivateRoute";
import MainLayout from "../layouts/MainLayout";
import { getDeliveries, registerReceipt, confirmDeliveryAction } from "../services/deliveryService";
import ReceiptModal from "../components/ReceiptModal";
import DeliveryActionModal from "../components/DeliveryActionModal";
import {
    FiMapPin, FiPhone, FiUser, FiChevronDown,
    FiPackage, FiClock, FiNavigation, FiCheck, FiDollarSign,
    FiTruck, FiRefreshCw, FiFilter, FiShoppingBag, FiCopy
} from 'react-icons/fi';
import { GiTakeMyMoney } from 'react-icons/gi';
import Spinner from "../components/Spinner";
import Swal from "sweetalert2";

// تابع بررسی وجود مختصات معتبر
const hasValidCoordinates = (order) => {
    const lat = parseFloat(order?.latitude);
    const lng = parseFloat(order?.longitude);

    return order?.latitude &&
        order?.longitude &&
        !isNaN(lat) &&
        !isNaN(lng) &&
        lat !== 0 &&
        lng !== 0;
};

const DeliveryContent = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [navigatingId, setNavigatingId] = useState(null);
    const [pagination, setPagination] = useState(null);

    // مودال ثبت دریافتی
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // مودال عملیات تحویل/برگشت
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [isActionSubmitting, setIsActionSubmitting] = useState(false);

    // state های فیلتر
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    const resetFilters = () => {
        setFilters({
            status: '',
            search: ''
        });
    };

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);

            const response = await getDeliveries(params);

            // با ساختار جدید، response همان بدنه خروجی لاراول است
            setOrders(response.data || []);
            setPagination(response.meta || null);
        } catch (err) {
            // دریافت پیام خطا مستقیما از سرویس
            const errorMessage = err.message || 'خطا در دریافت لیست سفارشات';
            setError(errorMessage);
            console.error('Error fetching orders:', err);
        } finally {
            setLoading(false);
        }
    }, [filters.status, filters.search]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const openReceiptModal = (order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedOrder(null);
        setIsSubmitting(false);
    };

    const openActionModal = (order, type) => {
        setSelectedOrder(order);
        setActionType(type);
        setIsActionModalOpen(true);
    };

    const closeActionModal = () => {
        setIsActionModalOpen(false);
        setSelectedOrder(null);
        setActionType(null);
        setIsActionSubmitting(false);
    };

    const handleSubmitReceipt = async (formData) => {
        setIsSubmitting(true);
        try {
            const response = await registerReceipt(formData);
            await fetchOrders();

            closeModal();
        } catch (err) {

            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitAction = async (formData) => {
        setIsActionSubmitting(true);
        try {
            const response = await confirmDeliveryAction(formData);
            await fetchOrders();
            Swal.fire({
                icon: 'success',
                title:"موفق",
                text:response.message,
            });
            closeActionModal();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title:"خطا",
                text:err.data.message,
            });
            console.error(err);
        } finally {
            setIsActionSubmitting(false);
        }
    };

    const handleNavigate = (order) => {
        if (!hasValidCoordinates(order)) {
            alert('موقعیت مکانی برای این سفارش ثبت نشده است!');
            return;
        }

        setNavigatingId(order.id);

        const latitude = parseFloat(order.latitude);
        const longitude = parseFloat(order.longitude);

        const geoUrl = `geo:${latitude},${longitude}`;

        window.open(geoUrl, '_blank');

        setNavigatingId(null);
    };

    if (loading) {
        return <Spinner />;
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-96 gap-4 mx-4">
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 text-center max-w-md">
                    <div className="text-6xl mb-4">😞</div>
                    <p className="text-red-600 font-medium mb-4">{error}</p>
                    <button
                        onClick={fetchOrders}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
                    >
                        تلاش مجدد
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div dir='rtl' className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-blue-50/30">
            <div className="max-w-5xl mx-auto px-4 py-6">
                {/* هدر صفحه */}
                <div className="mb-6">
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/60 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                                    <FiShoppingBag className="text-white text-2xl" />
                                </div>
                                <div>
                                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
                                        مدیریت سفارشات
                                    </h1>
                                    <p className="text-gray-500 text-sm mt-0.5">
                                        {pagination?.total?.toLocaleString('fa-IR') || orders.length} سفارش یافت شد
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={fetchOrders}
                                    className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition"
                                    title="بروزرسانی"
                                >
                                    <FiRefreshCw size={18} className="text-gray-600" />
                                </button>
                                <div className="hidden md:flex items-center gap-2 bg-blue-50/80 px-4 py-2 rounded-full">
                                    <FiClock className="text-blue-600 text-sm" />
                                    <span className="text-blue-700 text-sm font-medium">آخرین به‌روزرسانی: امروز</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* بخش فیلتر */}
                <div className="mb-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="w-full px-5 py-3.5 flex items-center justify-between bg-gray-50/50 hover:bg-gray-100 transition-all duration-200"
                        >
                            <div className="flex items-center gap-2.5">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${(filters.status || filters.search) ? 'bg-blue-500' : 'bg-blue-100'}`}>
                                    <FiFilter className={`text-sm transition-all duration-200 ${(filters.status || filters.search) ? 'text-white' : 'text-blue-600'}`} />
                                </div>
                                <span className="font-medium text-gray-700">فیلتر و جستجو</span>
                                {(filters.status || filters.search) && (
                                    <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-medium">
                                        فعال
                                    </span>
                                )}
                            </div>
                            <FiChevronDown
                                className={`text-gray-400 transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`}
                                size={18}
                            />
                        </button>

                        <div className={`transition-all duration-300 overflow-hidden ${showFilters ? 'max-h-96' : 'max-h-0'}`}>
                            <div className="p-5 border-t border-gray-100 bg-white">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1.5 font-medium">جستجو در شماره سفارش / مشتری</label>
                                        <input
                                            type="text"
                                            value={filters.search}
                                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                            placeholder="شماره سفارش یا نام مشتری..."
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm bg-gray-50/50 hover:bg-white"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1.5 font-medium">وضعیت سفارش</label>
                                        <select
                                            value={filters.status}
                                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm bg-gray-50/50 hover:bg-white cursor-pointer"
                                        >
                                            <option value="">همه وضعیت‌ها</option>
                                            <option value="pending">⏳ در انتظار</option>
                                            <option value="processing">🔄 در حال پردازش</option>
                                            <option value="in_progress">🚚 در حال ارسال</option>
                                            <option value="delivered">✅ تحویل شده</option>
                                            <option value="returned">↩️ برگشت خورده</option>
                                            <option value="cancelled">❌ لغو شده</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                                    <button
                                        onClick={resetFilters}
                                        className="px-5 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 hover:text-gray-700 transition-all duration-200 font-medium"
                                    >
                                        حذف همه فیلترها
                                    </button>
                                    <button
                                        onClick={() => setShowFilters(false)}
                                        className="px-5 py-2 text-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                                    >
                                        اعمال و بستن
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* لیست سفارشات */}
                {orders.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FiShoppingBag className="text-gray-400 text-4xl" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">هیچ سفارشی یافت نشد</h3>
                        <p className="text-gray-500">با تغییر فیلترها ممکن است نتایج بیشتری پیدا کنید</p>
                        {(filters.status || filters.search) && (
                            <button
                                onClick={resetFilters}
                                className="mt-4 px-5 py-2 text-sm text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition-all font-medium"
                            >
                                حذف فیلترها
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {orders.map((order) => (
                            <OrderCard
                                key={order.id}
                                order={order}
                                onOpenReceiptModal={openReceiptModal}
                                onOpenActionModal={openActionModal}
                                onNavigate={handleNavigate}
                                isNavigating={navigatingId === order.id}
                            />
                        ))}
                    </div>
                )}
            </div>

            <ReceiptModal
                isOpen={isModalOpen}
                order={selectedOrder}
                onClose={closeModal}
                onSubmit={handleSubmitReceipt}
                isSubmitting={isSubmitting}
            />

            <DeliveryActionModal
                isOpen={isActionModalOpen}
                order={selectedOrder}
                type={actionType}
                onClose={closeActionModal}
                onSubmit={handleSubmitAction}
                isSubmitting={isActionSubmitting}
            />
        </div>
    );
};

// کارت نمایش سفارش
const OrderCard = React.memo(({ order, onOpenReceiptModal, onOpenActionModal, onNavigate, isNavigating }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const formatPrice = React.useCallback((price) => {
        if (!price && price !== 0) return '0';
        return new Intl.NumberFormat('fa-IR').format(price);
    }, []);

    const getStatusColor = React.useCallback((statusText) => {
        const colors = {
            'تحویل شده': 'bg-green-100 text-green-700',
            'در انتظار': 'bg-yellow-100 text-yellow-700',
            'در حال ارسال': 'bg-blue-100 text-blue-700',
            'برگشت خورده': 'bg-red-100 text-red-700',
        };
        return colors[statusText] || 'bg-gray-100 text-gray-700';
    }, []);

    const completionPercentage = order.price > 0
        ? (order.confirmed_amount / order.price) * 100
        : 0;

    const copyAddress = (address) => {
        navigator.clipboard.writeText(address);
        const toast = document.createElement('div');
        toast.textContent = '✅ آدرس کپی شد';
        toast.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: #1f2937;
            color: white;
            padding: 8px 16px;
            border-radius: 8px;
            font-size: 14px;
            z-index: 10001;
            animation: fadeOut 2s ease;
        `;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 2000);
    };

    return (
        <div
            className={`group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100/80 hover:border-blue-200/50 ${isExpanded ? 'shadow-lg' : ''}`}
        >
            <div className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <div className="w-5 h-5 bg-blue-100 rounded-md flex items-center justify-center">
                                        <span className="text-blue-600 text-xs font-bold">#</span>
                                    </div>
                                    <p className="text-xs text-gray-400">شماره سفارش</p>
                                </div>
                                <p className="font-bold text-gray-800 text-lg">{order.id}</p>
                            </div>

                            <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <FiUser size={12} className="text-purple-500" />
                                    <p className="text-xs text-gray-400">مشتری</p>
                                </div>
                                <p className="font-semibold text-gray-800 text-sm truncate">
                                    {order.customer_name || '-'}
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <GiTakeMyMoney size={12} className="text-green-500" />
                                    <p className="text-xs text-gray-400">مبلغ کل</p>
                                </div>
                                <p className="font-bold text-green-600 text-sm">
                                    {formatPrice(order.price)} <span className="text-xs">ریال</span>
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <FiPackage size={12} className="text-orange-500" />
                                    <p className="text-xs text-gray-400">کد واحد</p>
                                </div>
                                <p className="font-mono font-semibold text-gray-700 text-sm">
                                    {order.unit_id || '-'}
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <div className="w-5 h-5 bg-gray-100 rounded-md flex items-center justify-center">
                                        <span className="text-gray-600 text-xs">✓</span>
                                    </div>
                                    <p className="text-xs text-gray-400">وضعیت</p>
                                </div>
                                <p className={`font-semibold text-sm px-2 py-0.5 rounded-full inline-block ${getStatusColor(order.order_status_text)}`}>
                                    {order.order_status_text || '-'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 justify-end">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-all"
                        >
                            <FiChevronDown
                                className={`text-gray-500 transition-all duration-300 ${isExpanded ? 'rotate-180 text-blue-600' : ''}`}
                                size={18}
                            />
                        </button>
                    </div>
                </div>
            </div>

            <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[700px]' : 'max-h-0'}`}>
                <div className="border-t border-gray-100 bg-gradient-to-br from-gray-50/80 to-white p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-1 h-5 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
                        <p className="text-sm font-semibold text-gray-700">جزئیات سفارش</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FiMapPin className="text-amber-600" size={14} />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs text-gray-500 mb-0.5">آدرس تحویل</p>
                                <div className="flex items-start gap-2">
                                    <p className="text-gray-700 text-sm leading-relaxed flex-1">{order.address || '-'}</p>
                                    {order.address && (
                                        <button
                                            onClick={() => copyAddress(order.address)}
                                            className="text-gray-400 hover:text-blue-600 transition p-1 flex-shrink-0"
                                            title="کپی آدرس"
                                        >
                                            <FiCopy size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FiPhone className="text-green-600" size={14} />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-0.5">شماره تماس</p>
                                <p className="text-gray-700 text-sm font-medium">{order.phone_number || '-'}</p>
                            </div>
                        </div>

                        {order.request_product_store_id && (
                            <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm md:col-span-2">
                                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FiPackage className="text-purple-600" size={14} />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-0.5">کد درخواست محصول</p>
                                    <p className="text-gray-700 text-sm font-mono">{order.request_product_store_id}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <FiDollarSign className="text-blue-600" />
                            اطلاعات مالی (ریال)
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div>
                                <p className="text-xs text-gray-500">مبلغ کل فاکتور</p>
                                <p className="font-bold text-gray-800">{formatPrice(order.price)} <span className="text-xs">ریال</span></p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">مبلغ پرداختی کل</p>
                                <p className="font-semibold text-green-600">{formatPrice(order.total_paid_amount)} <span className="text-xs">ریال</span></p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">مبلغ تأیید شده</p>
                                <p className="font-semibold text-blue-600">{formatPrice(order.confirmed_amount)} <span className="text-xs">ریال</span></p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">مبلغ باقیمانده</p>
                                <p className="font-semibold text-amber-600">{formatPrice(order.remaining_amount)} <span className="text-xs">ریال</span></p>
                            </div>
                        </div>

                        <div className="mt-3">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>دریافتی ثبت شده</span>
                                <span>{Math.round(completionPercentage)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(completionPercentage, 100)}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* دکمه‌های عملیات */}
                    <div className="mt-5 pt-3 border-t border-gray-200">
                        {/* دسکتاپ: ردیفی */}
                        <div className="hidden sm:flex sm:flex-row gap-3">
                            <button
                                onClick={() => onOpenReceiptModal(order)}
                                disabled={order.remaining_amount <= 0}
                                className={`flex-1 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg ${
                                    order.remaining_amount <= 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                                }`}
                            >
                                <FiCheck size={16} />
                                <span>
                                    {order.remaining_amount <= 0
                                        ? '✓ تکمیل شده'
                                        : `افزودن دریافتی (${formatPrice(order.remaining_amount)} ریال)`}
                                </span>
                            </button>

                            <button
                                onClick={() => onOpenActionModal(order, 'delivered')}
                                className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                            >
                                <FiTruck size={16} />
                                <span>تحویل شد</span>
                            </button>

                            <button
                                onClick={() => onOpenActionModal(order, 'returned')}
                                className="flex-1 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:from-red-700 hover:to-rose-700 transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                            >
                                <FiRefreshCw size={16} />
                                <span>برگشت مرسوله</span>
                            </button>

                            <button
                                onClick={() => onNavigate(order)}
                                disabled={isNavigating || !hasValidCoordinates(order)}
                                className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isNavigating ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>در حال باز کردن...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiNavigation size={16} />
                                        <span>مسیریابی</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* موبایل: شبکه 2×2 */}
                        <div className="grid grid-cols-2 gap-3 sm:hidden">
                            <button
                                onClick={() => onOpenReceiptModal(order)}
                                disabled={order.remaining_amount <= 0}
                                className={`py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg ${
                                    order.remaining_amount <= 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                                }`}
                            >
                                <FiCheck size={16} />
                                <span className="text-xs">دریافتی</span>
                            </button>

                            <button
                                onClick={() => onOpenActionModal(order, 'delivered')}
                                className="py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                            >
                                <FiTruck size={16} />
                                <span className="text-xs">تحویل</span>
                            </button>

                            <button
                                onClick={() => onOpenActionModal(order, 'returned')}
                                className="py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:from-red-700 hover:to-rose-700 transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                            >
                                <FiRefreshCw size={16} />
                                <span className="text-xs">برگشت</span>
                            </button>

                            <button
                                onClick={() => onNavigate(order)}
                                disabled={isNavigating || !hasValidCoordinates(order)}
                                className="py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2 text-sm font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isNavigating ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                ) : (
                                    <>
                                        <FiNavigation size={16} />
                                        <span className="text-xs">مسیر</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

OrderCard.displayName = 'OrderCard';

const Delivery = () => {
    return (
        <PrivateRoute>
            <MainLayout>
                <DeliveryContent />
            </MainLayout>
        </PrivateRoute>
    );
};

export default Delivery;